'use client';

import React from 'react';
import { ErrorInfo } from 'react';
import { noop } from 'lodash';
import { logSentryException } from '@/core/utils/sentry';
import { ErrorPlaceholder, type ErrorPlaceholderProps } from './ErrorPlaceholder';

export type OnError = (err: Error) => void;

export interface ErrorBoundaryProps extends React.PropsWithChildren {
  fallback?: React.ReactNode;
  showReason?: boolean;
  errorHeight?: ErrorPlaceholderProps['height'];
  onError?: OnError;
}

export interface ErrorBoundaryState {
  hasError: boolean;
  message?: string;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static defaultProps: ErrorBoundaryProps = {
    fallback: <ErrorPlaceholder />,
    onError: noop,
  };

  static getDerivedStateFromError(err: Error): ErrorBoundaryState {
    return { hasError: true, message: err?.message };
  }

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = { hasError: false };
  }

  override componentDidCatch(err: Error, info: ErrorInfo): void {
    logSentryException({ message: err.message, place: 'componentDidCatch', data: info });
    if (this.props.onError) {
      this.props.onError(err);
    }
  }

  render() {
    const { hasError } = this.state;

    if (hasError) {
      if (this.props.fallback === ErrorBoundary.defaultProps.fallback) {
        return React.cloneElement(this.props.fallback as any, {
          height: this.props.errorHeight,
          title: this.props.showReason ? this.state.message : undefined,
        } satisfies ErrorPlaceholderProps);
      }
      return this.props.fallback || null;
    }

    return this.props.children;
  }
}
